import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import apiClient, { API_BASE_URL, getUploadUrl } from '@/utils/api';
import { useAuth } from '@/utils/auth';
import { formatCurrency } from '@/utils/format';
import AdminLayout from '@/components/AdminLayout';
import MemberAvatar from '@/components/MemberAvatar';
import { Search, Edit, Eye, Download, DollarSign, FileText, Image, File, ExternalLink, Trash2, Plus, UserCog, Key, UserPlus, User, Users, X, Upload, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, MoreVertical, TrendingUp } from 'lucide-react';

const BACKEND_URL = API_BASE_URL;

const AdminMembers = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const impersonateMember = async (member) => {
    try {
      const res = await apiClient.post(`/admin/members/${member.member_id}/impersonate`);
      const adminToken = localStorage.getItem('jwt_token');
      localStorage.setItem('admin_token', adminToken);
      localStorage.setItem('jwt_token', res.data.token);
      localStorage.setItem('impersonation', JSON.stringify({
        memberName: res.data.member_name,
        adminName: res.data.admin_name,
      }));
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log in as member');
    }
  };
  const createMemberLogin = async (member) => {
    try {
      const res = await apiClient.post(`/admin/members/${member.member_id}/create-login`);
      const emailNote = res.data.welcome_email_sent
        ? 'Credentials emailed to the member.'
        : 'Email not sent — check SMTP settings.';
      toast.success(`Login account ready for ${member.first_name}. ${emailNote}`);
      loadMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create login account');
    }
  };

  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [missingLogins, setMissingLogins] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const openBulkInvite = async () => {
    setBulkInviteOpen(true);
    setMissingLogins(null);
    try {
      const res = await apiClient.get('/admin/login-accounts/missing');
      setMissingLogins(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check member login accounts');
      setBulkInviteOpen(false);
    }
  };

  const runBulkInvite = async () => {
    setBulkBusy(true);
    try {
      const res = await apiClient.post('/admin/login-accounts/bulk-create?send_invites=true');
      toast.success(`${res.data.message}. ${res.data.invites_queued} invite email(s) being sent.`);
      if (res.data.failed?.length) {
        toast.warning(`${res.data.failed.length} member(s) skipped (no email address)`);
      }
      setBulkInviteOpen(false);
      loadMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create login accounts');
    } finally {
      setBulkBusy(false);
    }
  };

  const repairLives = async () => {
    setBulkBusy(true);
    try {
      const res = await apiClient.post('/admin/beneficiaries/repair-lives');
      toast.success(`Lives recalculated — ${res.data.members_repaired} member(s) updated`);
      loadMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to recalculate lives');
    } finally {
      setBulkBusy(false);
    }
  };

  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [selectedMember, setSelectedMember] = useState(null);
  const [viewingMember, setViewingMember] = useState(null);
  const [memberBalance, setMemberBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [editForm, setEditForm] = useState({
    // Personal info
    username: '',
    title: '',
    first_name: '',
    last_name: '',
    gender: 'Other',
    email: '',
    contact_number: '',
    date_of_birth: '',
    nrc_passport: '',
    // Professional
    work_station: '',
    specialty: '',
    residential_address: '',
    // Membership
    membership_type: 'Standard',
    // Next of Kin
    next_of_kin: {
      surname: '',
      first_name: '',
      gender: 'Other',
      relationship: '',
      residential_address: '',
      contact_number: '',
      email: ''
    },
    // Beneficiaries
    beneficiaries: []
  });
  
  // New state for additional dialogs
  const [deletingMember, setDeletingMember] = useState(null);
  const [changingStatus, setChangingStatus] = useState(null);
  const [renewingMember, setRenewingMember] = useState(null);
  const [addingPayment, setAddingPayment] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    payment_reference: '',
    notes: ''
  });

  // Add Member state
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberTab, setAddMemberTab] = useState('personal');
  const [addMemberForm, setAddMemberForm] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    title: 'Mr',
    gender: 'Male',
    email: '',
    contact_number: '',
    date_of_birth: '',
    nrc_passport: '',
    residential_address: '',
    // Professional
    work_station: '',
    specialty: '',
    membership_type: 'Gold',
    status: 'active',
    membership_start_date: new Date().toISOString().split('T')[0],
    member_id: '',
    // Next of Kin
    next_of_kin: {
      surname: '',
      first_name: '',
      gender: 'Other',
      relationship: '',
      residential_address: '',
      contact_number: '',
      email: ''
    },
    // Beneficiaries
    beneficiaries: []
  });
  const [membershipTypes, setMembershipTypes] = useState(['Gold']);

  // Bulk Import state
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    loadMembers();
    loadMembershipTypes();
  }, []);

  useEffect(() => {
    filterMembers();
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [searchTerm, statusFilter, members]);

  // Fetch balance when viewing a member
  useEffect(() => {
    if (viewingMember) {
      loadMemberBalance(viewingMember.member_id);
    } else {
      setMemberBalance(null);
    }
  }, [viewingMember]);

  const loadMembershipTypes = async () => {
    try {
      const response = await apiClient.get('/settings/membership-types');
      if (response.data.membership_types && response.data.membership_types.length > 0) {
        setMembershipTypes(response.data.membership_types);
      }
    } catch (error) {
      // Use defaults
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);
  const showingStart = filteredMembers.length > 0 ? startIndex + 1 : 0;
  const showingEnd = Math.min(endIndex, filteredMembers.length);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of members list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const loadMemberBalance = async (memberId) => {
    setLoadingBalance(true);
    try {
      const response = await apiClient.get(`/payments/member/${memberId}/balance`);
      setMemberBalance(response.data);
    } catch (error) {
      console.error('Failed to load balance:', error);
      setMemberBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  const loadMembers = async () => {
    try {
      const response = await apiClient.get('/admin/members');
      setMembers(response.data);
      setFilteredMembers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const filterMembers = () => {
    let filtered = members;

    if (searchTerm) {
      filtered = filtered.filter(m => 
        m.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.member_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(m => m.status === statusFilter);
    }

    setFilteredMembers(filtered);
  };

  const exportToCSV = async () => {
    try {
      const response = await apiClient.get('/reports/members/csv', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `members_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Members exported successfully!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const exportFullToCSV = async () => {
    try {
      const response = await apiClient.get('/reports/members/csv/full', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `members_full_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Full member data exported (including Next of Kin & Beneficiaries)!');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    setEditForm({
      // Personal info
      username: member.username || '',
      title: member.title || '',
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      gender: member.gender || 'Other',
      email: member.email || '',
      contact_number: member.contact_number || '',
      date_of_birth: member.date_of_birth || '',
      nrc_passport: member.nrc_passport || '',
      // Professional
      work_station: member.work_station || '',
      specialty: member.specialty || '',
      residential_address: member.residential_address || '',
      // Membership
      membership_type: member.membership_type || 'Standard',
      // Next of Kin
      next_of_kin: {
        surname: member.next_of_kin?.surname || '',
        first_name: member.next_of_kin?.first_name || '',
        gender: member.next_of_kin?.gender || 'Other',
        relationship: member.next_of_kin?.relationship || '',
        residential_address: member.next_of_kin?.residential_address || '',
        contact_number: member.next_of_kin?.contact_number || '',
        email: member.next_of_kin?.email || ''
      },
      // Beneficiaries
      beneficiaries: member.beneficiaries || []
    });
  };

  const handleUpdateMember = async () => {
    try {
      await apiClient.put(`/admin/members/${selectedMember.member_id}/details`, editForm);
      toast.success('Member updated successfully!');
      setSelectedMember(null);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  // Helper functions for Next of Kin
  const updateNextOfKin = (field, value) => {
    setEditForm({
      ...editForm,
      next_of_kin: {
        ...editForm.next_of_kin,
        [field]: value
      }
    });
  };

  // Helper functions for Beneficiaries
  const addBeneficiary = () => {
    setEditForm({
      ...editForm,
      beneficiaries: [
        ...editForm.beneficiaries,
        { full_name: '', date_of_birth: '', age: 0, relationship: '' }
      ]
    });
  };

  const updateBeneficiary = (index, field, value) => {
    const updated = [...editForm.beneficiaries];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate age if date_of_birth is updated
    if (field === 'date_of_birth' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      updated[index].age = age >= 0 ? age : 0;
    }
    
    setEditForm({ ...editForm, beneficiaries: updated });
  };

  const removeBeneficiary = (index) => {
    const updated = editForm.beneficiaries.filter((_, i) => i !== index);
    setEditForm({ ...editForm, beneficiaries: updated });
  };

  const handleStatusChange = async (newStatus) => {
    if (!changingStatus) return;
    try {
      await apiClient.put(`/admin/members/${changingStatus.member_id}/status?status=${newStatus}`);
      toast.success(`Member status changed to ${newStatus}`);
      setChangingStatus(null);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Status update failed');
    }
  };

  const handleDeleteMember = async () => {
    if (!deletingMember) return;
    try {
      await apiClient.delete(`/admin/members/${deletingMember.member_id}`);
      toast.success('Member deleted successfully');
      setDeletingMember(null);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleRenewMember = async () => {
    if (!renewingMember) return;
    try {
      const res = await apiClient.post(`/membership/members/${renewingMember.member_id}/renew`);
      toast.success(res.data?.message || 'Membership renewed');
      setRenewingMember(null);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Renewal failed');
    }
  };

  const handleAddPayment = async () => {
    if (!addingPayment || !paymentForm.amount) {
      toast.error('Please enter an amount');
      return;
    }
    try {
      const params = new URLSearchParams({
        amount: paymentForm.amount,
        payment_method: paymentForm.payment_method,
        ...(paymentForm.payment_reference && { payment_reference: paymentForm.payment_reference }),
        ...(paymentForm.notes && { notes: paymentForm.notes })
      });
      
      const response = await apiClient.post(`/admin/members/${addingPayment.member_id}/payments?${params}`);
      const data = response.data;
      
      let message = `Payment of $${paymentForm.amount} recorded successfully!`;
      if (data.invoices_paid?.length > 0) {
        message += ` ${data.invoices_paid.length} invoice(s) marked as paid.`;
      }
      if (data.credit_remaining > 0) {
        message += ` Credit balance: $${data.credit_remaining.toFixed(2)}`;
      }
      
      toast.success(message);
      setAddingPayment(null);
      setPaymentForm({ amount: '', payment_method: 'cash', payment_reference: '', notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleResetPassword = async () => {
    if (!resettingPassword) return;
    
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await apiClient.put(`/admin/members/${resettingPassword.member_id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success(`Password reset successfully for ${resettingPassword.first_name} ${resettingPassword.last_name}`);
      setResettingPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleAddMember = async () => {
    // Validate required fields
    if (!addMemberForm.first_name || !addMemberForm.last_name || !addMemberForm.email || !addMemberForm.contact_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addMemberForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setAddingMember(true);
    try {
      // Build request body with all member data
      const memberData = {
        first_name: addMemberForm.first_name,
        last_name: addMemberForm.last_name,
        email: addMemberForm.email,
        contact_number: addMemberForm.contact_number,
        membership_type: addMemberForm.membership_type || membershipTypes[0] || 'Gold',
        status: addMemberForm.status || 'active',
        membership_start_date: addMemberForm.membership_start_date,
        title: addMemberForm.title || 'Mr',
        gender: addMemberForm.gender || 'Male',
        date_of_birth: addMemberForm.date_of_birth || '',
        nrc_passport: addMemberForm.nrc_passport || '',
        residential_address: addMemberForm.residential_address || '',
        work_station: addMemberForm.work_station || '',
        specialty: addMemberForm.specialty || ''
      };

      // Only add member_id if provided
      if (addMemberForm.member_id) {
        memberData.member_id = addMemberForm.member_id;
      }

      // Add next of kin if provided
      if (addMemberForm.next_of_kin && addMemberForm.next_of_kin.first_name) {
        memberData.next_of_kin = addMemberForm.next_of_kin;
      }

      // Add beneficiaries if provided
      if (addMemberForm.beneficiaries && addMemberForm.beneficiaries.length > 0) {
        memberData.beneficiaries = addMemberForm.beneficiaries.filter(b => b.full_name);
      }

      await apiClient.post('/admin/members', memberData);
      toast.success('Member added successfully!');
      setAddMemberOpen(false);
      setAddMemberTab('personal');
      setAddMemberForm({
        first_name: '',
        last_name: '',
        title: 'Mr',
        gender: 'Male',
        email: '',
        contact_number: '',
        date_of_birth: '',
        nrc_passport: '',
        residential_address: '',
        work_station: '',
        specialty: '',
        membership_type: membershipTypes[0] || 'Gold',
        status: 'active',
        membership_start_date: new Date().toISOString().split('T')[0],
        member_id: '',
        next_of_kin: {
          surname: '',
          first_name: '',
          gender: 'Other',
          relationship: '',
          residential_address: '',
          contact_number: '',
          email: ''
        },
        beneficiaries: []
      });
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  // Bulk Import functions
  const downloadTemplate = async () => {
    try {
      const response = await apiClient.get('/bulk-import/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'member_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await apiClient.post('/bulk-import/members', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} members!`);
        loadMembers();
      }
      
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows had errors`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResult({
        error_count: 1,
        errors: [error.response?.data?.detail || 'Import failed'],
        imported_count: 0
      });
    } finally {
      setImporting(false);
    }
  };

  const closeBulkImport = () => {
    setBulkImportOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const viewPayments = (member) => {
    navigate(`/admin/members/${member.member_id}/payments`);
  };

  const generateInvoice = (member) => {
    navigate(`/admin/members/${member.member_id}/generate-invoice`);
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (fileType?.includes('image')) return <Image className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const getAttachmentLabel = (index) => {
    const labels = ['ID Copy', 'Portrait Photo', 'Proof of Payment'];
    return labels[index] || `Attachment ${index + 1}`;
  };

  const openFileViewer = (attachment, index) => {
    const url = getUploadUrl(attachment.file_path);
    if (!url) {
      toast.error('File not available');
      return;
    }
    if (attachment.file_type?.includes('image')) {
      setViewingFile({ ...attachment, url, label: getAttachmentLabel(index) });
    } else {
      window.open(url, '_blank');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return classes[status] || classes.pending;
  };

  return (
    <AdminLayout currentPage="members" title="Manage Members">
      {/* Filters and Actions */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, or member ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-members-input"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded px-4 py-2 w-full sm:w-auto"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={() => setAddMemberOpen(true)} data-testid="add-member-btn" className="w-full sm:w-auto">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
              <Button onClick={() => setBulkImportOpen(true)} variant="secondary" data-testid="bulk-import-btn" className="w-full sm:w-auto">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Import
              </Button>
              <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-btn" className="w-full sm:w-auto">
                <Download className="w-4 h-4 mr-2" />
                Export Basic
              </Button>
              <Button onClick={exportFullToCSV} variant="outline" data-testid="export-full-csv-btn" className="w-full sm:w-auto">
                <Download className="w-4 h-4 mr-2" />
                Export Full (NOK + Beneficiaries)
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="bulk-actions-btn" className="w-full sm:w-auto">
                    <UserCog className="w-4 h-4 mr-2" />
                    Bulk Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem onClick={openBulkInvite} data-testid="bulk-invite-logins-btn">
                    <Key className="w-4 h-4 mr-2" />
                    Create Missing Logins &amp; Invite
                  </DropdownMenuItem>
                  {currentUser?.role === 'super_admin' && (
                    <DropdownMenuItem onClick={repairLives} disabled={bulkBusy} data-testid="repair-lives-btn">
                      <Users className="w-4 h-4 mr-2" />
                      Recalculate Beneficiary Lives
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div className="text-sm text-muted-foreground">
              Showing {showingStart}-{showingEnd} of {filteredMembers.length} members
              {filteredMembers.length !== members.length && ` (filtered from ${members.length} total)`}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
                data-testid="items-per-page-select"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <span className="text-sm text-muted-foreground">per page</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Members Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : filteredMembers.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            No members found
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {paginatedMembers.map((member) => (
              <Card key={member.member_id} data-testid={`member-${member.member_id}`}>
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <MemberAvatar photoUrl={member.portrait_photo_url} firstName={member.first_name} lastName={member.last_name} size="xs" data-testid={`member-avatar-${member.member_id}`} />
                        <h3 className="text-base sm:text-lg font-semibold truncate">
                          {member.first_name} {member.last_name}
                        </h3>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(member.status)}`}>
                          {member.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground text-xs sm:text-sm">Member ID</p>
                          <p className="font-medium text-xs sm:text-sm truncate">{member.member_id}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs sm:text-sm">Email</p>
                          <p className="font-medium text-xs sm:text-sm truncate">{member.email}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs sm:text-sm">Contact</p>
                          <p className="font-medium text-xs sm:text-sm">{member.contact_number}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs sm:text-sm">Membership Type</p>
                          <p className="font-medium text-xs sm:text-sm">{member.membership_type}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1.5 sm:gap-2 flex-wrap lg:justify-end items-center">
                      <Button size="sm" variant="outline" onClick={() => setViewingMember(member)} title="View Details" data-testid={`view-${member.member_id}`} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                        <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleEdit(member)} title="Edit Member" data-testid={`edit-${member.member_id}`} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                        <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => viewPayments(member)} title="View Payments" data-testid={`payments-${member.member_id}`} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                        <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setAddingPayment(member)} title="Add Payment" data-testid={`add-payment-${member.member_id}`} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                        <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => generateInvoice(member)} title="Generate Invoice" data-testid={`invoice-${member.member_id}`} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                        <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" title="More actions" data-testid={`more-${member.member_id}`} className="h-8 w-8 sm:h-9 sm:w-9 p-0">
                            <MoreVertical className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => setRenewingMember(member)} data-testid={`renew-${member.member_id}`}>
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Renew Membership
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setChangingStatus(member)} data-testid={`status-${member.member_id}`}>
                            <UserCog className="w-4 h-4 mr-2" />
                            Change Status
                          </DropdownMenuItem>
                          {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
                            <DropdownMenuItem onClick={() => impersonateMember(member)} data-testid={`impersonate-${member.member_id}`}>
                              <User className="w-4 h-4 mr-2" />
                              Log in as Member
                            </DropdownMenuItem>
                          )}
                          {!member.user_id && (
                            <DropdownMenuItem onClick={() => createMemberLogin(member)} data-testid={`create-login-${member.member_id}`}>
                              <Key className="w-4 h-4 mr-2" />
                              Create Login Account
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setDeletingMember(member)} className="text-destructive focus:text-destructive" data-testid={`delete-${member.member_id}`}>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Member
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Card className="mt-4">
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      data-testid="first-page-btn"
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    
                    {/* Page number buttons */}
                    <div className="hidden sm:flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className="w-9 h-9 p-0"
                            data-testid={`page-${pageNum}-btn`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      data-testid="next-page-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      data-testid="last-page-btn"
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Member: {selectedMember?.first_name} {selectedMember?.last_name}</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="personal">
                <User className="w-4 h-4 mr-1 hidden sm:inline" />
                Personal
              </TabsTrigger>
              <TabsTrigger value="professional">
                <FileText className="w-4 h-4 mr-1 hidden sm:inline" />
                Professional
              </TabsTrigger>
              <TabsTrigger value="nextofkin">
                <Users className="w-4 h-4 mr-1 hidden sm:inline" />
                Next of Kin
              </TabsTrigger>
              <TabsTrigger value="beneficiaries">
                <Users className="w-4 h-4 mr-1 hidden sm:inline" />
                Beneficiaries
              </TabsTrigger>
            </TabsList>

            {/* Personal Information Tab */}
            <TabsContent value="personal" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={editForm.title || ''} 
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  >
                    <option value="">Select</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>
                <div>
                  <Label>Gender</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={editForm.gender || 'Other'} 
                    onChange={(e) => setEditForm({...editForm, gender: e.target.value})}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input 
                    value={editForm.first_name || ''} 
                    onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} 
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input 
                    value={editForm.last_name || ''} 
                    onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} 
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Username</Label>
                  <Input 
                    value={editForm.username || ''} 
                    onChange={(e) => setEditForm({...editForm, username: e.target.value})} 
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={editForm.email || ''} 
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})} 
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Number *</Label>
                  <Input 
                    value={editForm.contact_number || ''} 
                    onChange={(e) => setEditForm({...editForm, contact_number: e.target.value})} 
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input 
                    type="date"
                    value={editForm.date_of_birth || ''} 
                    onChange={(e) => setEditForm({...editForm, date_of_birth: e.target.value})} 
                  />
                </div>
              </div>
              <div>
                <Label>NRC/Passport Number</Label>
                <Input 
                  value={editForm.nrc_passport || ''} 
                  onChange={(e) => setEditForm({...editForm, nrc_passport: e.target.value})} 
                />
              </div>
              <div>
                <Label>Residential Address</Label>
                <Input 
                  value={editForm.residential_address || ''} 
                  onChange={(e) => setEditForm({...editForm, residential_address: e.target.value})} 
                />
              </div>
            </TabsContent>

            {/* Professional Information Tab */}
            <TabsContent value="professional" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Work Station</Label>
                  <Input 
                    value={editForm.work_station || ''} 
                    onChange={(e) => setEditForm({...editForm, work_station: e.target.value})} 
                  />
                </div>
                <div>
                  <Label>Specialty</Label>
                  <Input 
                    value={editForm.specialty || ''} 
                    onChange={(e) => setEditForm({...editForm, specialty: e.target.value})} 
                  />
                </div>
              </div>
              <div>
                <Label>Membership Type</Label>
                <select 
                  className="w-full border rounded p-2" 
                  value={editForm.membership_type || membershipTypes[0] || 'Gold'} 
                  onChange={(e) => setEditForm({...editForm, membership_type: e.target.value})}
                  data-testid="edit-member-type"
                >
                  {membershipTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              {/* Password Reset Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Password Management
                    </h4>
                    <p className="text-sm text-muted-foreground">Reset the member&apos;s login password</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setResettingPassword(selectedMember);
                      setSelectedMember(null);
                    }}
                    data-testid="reset-password-btn"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Reset Password
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Next of Kin Tab */}
            <TabsContent value="nextofkin" className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">Emergency contact information</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input 
                    value={editForm.next_of_kin?.first_name || ''} 
                    onChange={(e) => updateNextOfKin('first_name', e.target.value)} 
                  />
                </div>
                <div>
                  <Label>Surname</Label>
                  <Input 
                    value={editForm.next_of_kin?.surname || ''} 
                    onChange={(e) => updateNextOfKin('surname', e.target.value)} 
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Gender</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={editForm.next_of_kin?.gender || 'Other'} 
                    onChange={(e) => updateNextOfKin('gender', e.target.value)}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Relationship</Label>
                  <Input 
                    value={editForm.next_of_kin?.relationship || ''} 
                    onChange={(e) => updateNextOfKin('relationship', e.target.value)} 
                    placeholder="e.g., Spouse, Parent, Sibling"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Number</Label>
                  <Input 
                    value={editForm.next_of_kin?.contact_number || ''} 
                    onChange={(e) => updateNextOfKin('contact_number', e.target.value)} 
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={editForm.next_of_kin?.email || ''} 
                    onChange={(e) => updateNextOfKin('email', e.target.value)} 
                  />
                </div>
              </div>
              <div>
                <Label>Residential Address</Label>
                <Input 
                  value={editForm.next_of_kin?.residential_address || ''} 
                  onChange={(e) => updateNextOfKin('residential_address', e.target.value)} 
                />
              </div>
            </TabsContent>

            {/* Beneficiaries Tab */}
            <TabsContent value="beneficiaries" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-muted-foreground">
                  {editForm.beneficiaries?.length || 0} beneficiary(ies)
                </p>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={addBeneficiary}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Beneficiary
                </Button>
              </div>
              
              {editForm.beneficiaries?.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-muted/50">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No beneficiaries added</p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={addBeneficiary}
                  >
                    Add First Beneficiary
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {editForm.beneficiaries.map((ben, index) => (
                    <div key={index} className="border rounded-lg p-4 relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 h-8 w-8 p-0 text-destructive hover:text-destructive"
                        onClick={() => removeBeneficiary(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <p className="text-sm font-medium mb-3">Beneficiary {index + 1}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Full Name *</Label>
                          <Input 
                            value={ben.full_name || ''} 
                            onChange={(e) => updateBeneficiary(index, 'full_name', e.target.value)} 
                            placeholder="Full name"
                          />
                        </div>
                        <div>
                          <Label>Relationship *</Label>
                          <Input 
                            value={ben.relationship || ''} 
                            onChange={(e) => updateBeneficiary(index, 'relationship', e.target.value)} 
                            placeholder="e.g., Child, Spouse"
                          />
                        </div>
                        <div>
                          <Label>Date of Birth</Label>
                          <Input 
                            type="date"
                            value={ben.date_of_birth || ''} 
                            onChange={(e) => updateBeneficiary(index, 'date_of_birth', e.target.value)} 
                          />
                        </div>
                        <div>
                          <Label>Age</Label>
                          <Input 
                            type="number"
                            value={ben.age || 0} 
                            onChange={(e) => updateBeneficiary(index, 'age', parseInt(e.target.value) || 0)} 
                            placeholder="Auto-calculated from DOB"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleUpdateMember} data-testid="save-member-changes-btn">Save All Changes</Button>
            <Button variant="outline" onClick={() => setSelectedMember(null)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewingMember} onOpenChange={() => setViewingMember(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          {viewingMember && (
            <div className="space-y-6">
              {/* Member Header with Photo */}
              <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
                <MemberAvatar
                  photoUrl={viewingMember.portrait_photo_url}
                  firstName={viewingMember.first_name}
                  lastName={viewingMember.last_name}
                  size="xl"
                  data-testid="member-photo"
                />
                <div className="flex-1">
                  <h3 className="text-xl font-bold">{viewingMember.title} {viewingMember.first_name} {viewingMember.last_name}</h3>
                  <p className="text-muted-foreground">{viewingMember.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(viewingMember.status)}`}>
                      {viewingMember.status.toUpperCase()}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">{viewingMember.member_id}</span>
                  </div>
                </div>
              </div>

              {/* Outstanding Balance Section */}
              <div className={`p-4 rounded-lg border-2 ${memberBalance?.outstanding_balance > 0 ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Outstanding Balance
                </h3>
                {loadingBalance ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    <span className="text-sm text-muted-foreground">Loading balance...</span>
                  </div>
                ) : memberBalance ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Due</p>
                      <p className="text-xl font-bold">{formatCurrency(memberBalance.total_annual_fee, memberBalance.currency)}</p>
                      <p className="text-xs text-muted-foreground">
                        ({formatCurrency(memberBalance.monthly_fee, memberBalance.currency)} × {memberBalance.membership_period_months} months)
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Paid</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(memberBalance.total_paid, memberBalance.currency)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                      <p className={`text-2xl font-bold ${memberBalance.outstanding_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(memberBalance.outstanding_balance, memberBalance.currency)}
                      </p>
                      {memberBalance.outstanding_balance > 0 && (
                        <p className="text-xs text-red-600 font-medium mt-1">Payment Required</p>
                      )}
                      {memberBalance.outstanding_balance === 0 && (
                        <p className="text-xs text-green-600 font-medium mt-1">Fully Paid</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Unable to load balance</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Gender</p>
                  <p className="font-medium">{viewingMember.gender || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{viewingMember.date_of_birth || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">NRC/Passport</p>
                  <p className="font-medium">{viewingMember.nrc_passport || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">{viewingMember.contact_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Work Station</p>
                  <p className="font-medium">{viewingMember.work_station}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Specialty</p>
                  <p className="font-medium">{viewingMember.specialty}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Residential Address</p>
                  <p className="font-medium">{viewingMember.residential_address || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-medium">
                    {viewingMember.created_at 
                      ? new Date(viewingMember.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Uploaded Documents */}
              {viewingMember.attachments && viewingMember.attachments.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Uploaded Documents</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {viewingMember.attachments.map((attachment, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-3 cursor-pointer hover:bg-muted transition-colors"
                        onClick={() => openFileViewer(attachment, index)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {getFileIcon(attachment.file_type)}
                          <span className="font-medium">{getAttachmentLabel(index)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{attachment.filename}</p>
                        {attachment.file_type?.includes('image') && (
                          <img
                            src={getUploadUrl(attachment.file_path)}
                            alt={getAttachmentLabel(index)}
                            className="mt-2 w-full h-32 object-cover rounded"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Next of Kin</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Name</p>
                    <p>{viewingMember.next_of_kin?.first_name} {viewingMember.next_of_kin?.surname}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Gender</p>
                    <p>{viewingMember.next_of_kin?.gender 
                      ? (viewingMember.next_of_kin.gender === 'Male' ? 'M' : viewingMember.next_of_kin.gender === 'Female' ? 'F' : viewingMember.next_of_kin.gender)
                      : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Relationship</p>
                    <p>{viewingMember.next_of_kin?.relationship}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Contact</p>
                    <p>{viewingMember.next_of_kin?.contact_number}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p>{viewingMember.next_of_kin?.email}</p>
                  </div>
                </div>
              </div>

              {viewingMember.beneficiaries && viewingMember.beneficiaries.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Beneficiaries</h3>
                  <div className="space-y-2">
                    {viewingMember.beneficiaries.map((ben, idx) => (
                      <div key={idx} className="p-3 border rounded">
                        <p><strong>{ben.full_name}</strong> - {ben.age} years ({ben.relationship})</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Status Dialog */}
      <Dialog open={!!changingStatus} onOpenChange={() => setChangingStatus(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Member Status</DialogTitle>
          </DialogHeader>
          {changingStatus && (
            <div className="space-y-4">
              <p>Change status for <strong>{changingStatus.first_name} {changingStatus.last_name}</strong></p>
              <p className="text-sm text-muted-foreground">
                Current status: <span className={`px-2 py-1 rounded text-xs ${getStatusBadgeClass(changingStatus.status)}`}>{changingStatus.status.toUpperCase()}</span>
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => handleStatusChange('active')} variant={changingStatus.status === 'active' ? 'default' : 'outline'} className="w-full">
                  Active
                </Button>
                <Button onClick={() => handleStatusChange('pending')} variant={changingStatus.status === 'pending' ? 'default' : 'outline'} className="w-full">
                  Pending
                </Button>
                <Button onClick={() => handleStatusChange('suspended')} variant={changingStatus.status === 'suspended' ? 'destructive' : 'outline'} className="w-full">
                  Suspended
                </Button>
                <Button onClick={() => handleStatusChange('cancelled')} variant={changingStatus.status === 'cancelled' ? 'destructive' : 'outline'} className="w-full">
                  Cancelled
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Renew Membership Dialog */}
      <Dialog open={!!renewingMember} onOpenChange={() => setRenewingMember(null)}>
        <DialogContent data-testid="renew-member-dialog">
          <DialogHeader>
            <DialogTitle>Renew Membership</DialogTitle>
          </DialogHeader>
          {renewingMember && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enroll <strong>{renewingMember.first_name} {renewingMember.last_name}</strong> into the next
                annual membership cycle. A renewal invoice for the full annual fee (no proration) will be
                generated. Current cycle: <strong>{renewingMember.cycle_year || 'unassigned'}</strong>.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRenewingMember(null)}>Cancel</Button>
                <Button className="bg-green-600 hover:bg-green-700" onClick={handleRenewMember} data-testid="confirm-renew-member-btn">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Confirm Renewal
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingMember} onOpenChange={() => setDeletingMember(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Member</DialogTitle>
          </DialogHeader>
          {deletingMember && (
            <div className="space-y-4">
              <p className="text-destructive">
                Are you sure you want to delete <strong>{deletingMember.first_name} {deletingMember.last_name}</strong>?
              </p>
              <p className="text-sm text-muted-foreground">
                This will permanently delete the member, their user account, all payment records, and invoices. This action cannot be undone.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeletingMember(null)}>Cancel</Button>
                <Button variant="destructive" onClick={handleDeleteMember}>Delete Member</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={!!addingPayment} onOpenChange={() => { setAddingPayment(null); setPaymentForm({ amount: '', payment_method: 'cash', payment_reference: '', notes: '' }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Offline Payment</DialogTitle>
          </DialogHeader>
          {addingPayment && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Recording payment for <strong>{addingPayment.first_name} {addingPayment.last_name}</strong>
              </p>
              
              <div>
                <Label htmlFor="amount">Amount ($) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                  data-testid="payment-amount"
                />
              </div>
              
              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <select
                  id="payment_method"
                  className="w-full border rounded p-2"
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                >
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="payment_reference">Reference Number (optional)</Label>
                <Input
                  id="payment_reference"
                  placeholder="e.g., Receipt number, cheque number"
                  value={paymentForm.payment_reference}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this payment"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                />
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => { setAddingPayment(null); setPaymentForm({ amount: '', payment_method: 'cash', payment_reference: '', notes: '' }); }}>
                  Cancel
                </Button>
                <Button onClick={handleAddPayment}>Record Payment</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={!!viewingFile} onOpenChange={() => setViewingFile(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewingFile?.label}</DialogTitle>
          </DialogHeader>
          {viewingFile && (
            <div className="flex flex-col items-center">
              <img
                src={viewingFile.url}
                alt={viewingFile.label}
                className="max-h-[60vh] object-contain rounded"
              />
              <p className="text-sm text-muted-foreground mt-2">{viewingFile.filename}</p>
              <Button
                className="mt-4"
                onClick={() => window.open(viewingFile.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resettingPassword} onOpenChange={() => { setResettingPassword(null); setNewPassword(''); setConfirmPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Reset Member Password
            </DialogTitle>
          </DialogHeader>
          {resettingPassword && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  You are resetting the password for <strong>{resettingPassword.first_name} {resettingPassword.last_name}</strong> ({resettingPassword.email})
                </p>
              </div>
              
              <div>
                <Label htmlFor="new-password">New Password *</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password (min 8 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password-input"
                />
              </div>
              
              <div>
                <Label htmlFor="confirm-password">Confirm Password *</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="confirm-password-input"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
              
              <div className="text-xs text-muted-foreground">
                <p>Password requirements:</p>
                <ul className="list-disc list-inside mt-1">
                  <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>At least 8 characters</li>
                </ul>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => { setResettingPassword(null); setNewPassword(''); setConfirmPassword(''); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleResetPassword}
                  disabled={!newPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                  data-testid="confirm-reset-password-btn"
                >
                  Reset Password
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog - Enhanced with Tabs */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New Member
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={addMemberTab} onValueChange={setAddMemberTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="personal">
                <User className="w-4 h-4 mr-1 hidden sm:inline" />
                Personal
              </TabsTrigger>
              <TabsTrigger value="professional">
                <FileText className="w-4 h-4 mr-1 hidden sm:inline" />
                Professional
              </TabsTrigger>
              <TabsTrigger value="nextofkin">
                <Users className="w-4 h-4 mr-1 hidden sm:inline" />
                Next of Kin
              </TabsTrigger>
              <TabsTrigger value="beneficiaries">
                <Users className="w-4 h-4 mr-1 hidden sm:inline" />
                Beneficiaries
              </TabsTrigger>
            </TabsList>

            {/* Personal Information Tab */}
            <TabsContent value="personal" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={addMemberForm.title || 'Mr'} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, title: e.target.value})}
                    data-testid="add-member-title"
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>
                <div>
                  <Label>Gender</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={addMemberForm.gender || 'Male'} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, gender: e.target.value})}
                    data-testid="add-member-gender"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input 
                    value={addMemberForm.first_name || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, first_name: e.target.value})}
                    placeholder="John"
                    data-testid="add-member-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input 
                    value={addMemberForm.last_name || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, last_name: e.target.value})}
                    placeholder="Doe"
                    data-testid="add-member-last-name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={addMemberForm.email || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, email: e.target.value})}
                    placeholder="john.doe@example.com"
                    data-testid="add-member-email"
                  />
                </div>
                <div>
                  <Label>Contact Number *</Label>
                  <Input 
                    value={addMemberForm.contact_number || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, contact_number: e.target.value})}
                    placeholder="+1-555-0123"
                    data-testid="add-member-phone"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date of Birth</Label>
                  <Input 
                    type="date"
                    value={addMemberForm.date_of_birth || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, date_of_birth: e.target.value})}
                    data-testid="add-member-dob"
                  />
                </div>
                <div>
                  <Label>NRC/Passport Number</Label>
                  <Input 
                    value={addMemberForm.nrc_passport || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, nrc_passport: e.target.value})}
                    placeholder="123456/78/9"
                    data-testid="add-member-nrc"
                  />
                </div>
              </div>
              <div>
                <Label>Residential Address</Label>
                <Input 
                  value={addMemberForm.residential_address || ''} 
                  onChange={(e) => setAddMemberForm({...addMemberForm, residential_address: e.target.value})}
                  placeholder="123 Main Street, City"
                  data-testid="add-member-address"
                />
              </div>
            </TabsContent>

            {/* Professional Information Tab */}
            <TabsContent value="professional" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Work Station</Label>
                  <Input 
                    value={addMemberForm.work_station || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, work_station: e.target.value})}
                    placeholder="Office/Department"
                    data-testid="add-member-workstation"
                  />
                </div>
                <div>
                  <Label>Specialty</Label>
                  <Input 
                    value={addMemberForm.specialty || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, specialty: e.target.value})}
                    placeholder="Area of expertise"
                    data-testid="add-member-specialty"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Membership Type</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={addMemberForm.membership_type || membershipTypes[0] || 'Gold'} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, membership_type: e.target.value})}
                    data-testid="add-member-type"
                  >
                    {membershipTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Status</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={addMemberForm.status || 'active'} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, status: e.target.value})}
                    data-testid="add-member-status"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Membership Start Date</Label>
                  <Input 
                    type="date"
                    value={addMemberForm.membership_start_date || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, membership_start_date: e.target.value})}
                    data-testid="add-member-start-date"
                  />
                </div>
                <div>
                  <Label>Member ID (optional)</Label>
                  <Input 
                    value={addMemberForm.member_id || ''} 
                    onChange={(e) => setAddMemberForm({...addMemberForm, member_id: e.target.value})}
                    placeholder="Leave blank to auto-generate"
                    data-testid="add-member-id"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">If Member ID is not provided, a unique ID will be generated automatically</p>
            </TabsContent>

            {/* Next of Kin Tab */}
            <TabsContent value="nextofkin" className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">Emergency contact information (optional for new members)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input 
                    value={addMemberForm.next_of_kin?.first_name || ''} 
                    onChange={(e) => setAddMemberForm({
                      ...addMemberForm, 
                      next_of_kin: {...addMemberForm.next_of_kin, first_name: e.target.value}
                    })}
                    data-testid="add-nok-first-name"
                  />
                </div>
                <div>
                  <Label>Surname</Label>
                  <Input 
                    value={addMemberForm.next_of_kin?.surname || ''} 
                    onChange={(e) => setAddMemberForm({
                      ...addMemberForm, 
                      next_of_kin: {...addMemberForm.next_of_kin, surname: e.target.value}
                    })}
                    data-testid="add-nok-surname"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Gender</Label>
                  <select 
                    className="w-full border rounded p-2" 
                    value={addMemberForm.next_of_kin?.gender || 'Other'} 
                    onChange={(e) => setAddMemberForm({
                      ...addMemberForm, 
                      next_of_kin: {...addMemberForm.next_of_kin, gender: e.target.value}
                    })}
                    data-testid="add-nok-gender"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Relationship</Label>
                  <Input 
                    value={addMemberForm.next_of_kin?.relationship || ''} 
                    onChange={(e) => setAddMemberForm({
                      ...addMemberForm, 
                      next_of_kin: {...addMemberForm.next_of_kin, relationship: e.target.value}
                    })}
                    placeholder="e.g., Spouse, Parent, Sibling"
                    data-testid="add-nok-relationship"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Number</Label>
                  <Input 
                    value={addMemberForm.next_of_kin?.contact_number || ''} 
                    onChange={(e) => setAddMemberForm({
                      ...addMemberForm, 
                      next_of_kin: {...addMemberForm.next_of_kin, contact_number: e.target.value}
                    })}
                    data-testid="add-nok-phone"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={addMemberForm.next_of_kin?.email || ''} 
                    onChange={(e) => setAddMemberForm({
                      ...addMemberForm, 
                      next_of_kin: {...addMemberForm.next_of_kin, email: e.target.value}
                    })}
                    data-testid="add-nok-email"
                  />
                </div>
              </div>
              <div>
                <Label>Residential Address</Label>
                <Input 
                  value={addMemberForm.next_of_kin?.residential_address || ''} 
                  onChange={(e) => setAddMemberForm({
                    ...addMemberForm, 
                    next_of_kin: {...addMemberForm.next_of_kin, residential_address: e.target.value}
                  })}
                  data-testid="add-nok-address"
                />
              </div>
            </TabsContent>

            {/* Beneficiaries Tab */}
            <TabsContent value="beneficiaries" className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-muted-foreground">
                  {addMemberForm.beneficiaries?.length || 0} beneficiary(ies) added
                </p>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setAddMemberForm({
                    ...addMemberForm,
                    beneficiaries: [...(addMemberForm.beneficiaries || []), { full_name: '', date_of_birth: '', age: 0, relationship: '' }]
                  })}
                  data-testid="add-beneficiary-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Beneficiary
                </Button>
              </div>
              
              {(!addMemberForm.beneficiaries || addMemberForm.beneficiaries.length === 0) ? (
                <div className="text-center py-8 border rounded-lg bg-muted/50">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No beneficiaries added (optional)</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {addMemberForm.beneficiaries.map((ben, index) => (
                    <div key={index} className="border rounded-lg p-4 relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 h-8 w-8 p-0 text-destructive hover:text-destructive"
                        onClick={() => {
                          const updated = addMemberForm.beneficiaries.filter((_, i) => i !== index);
                          setAddMemberForm({...addMemberForm, beneficiaries: updated});
                        }}
                        data-testid={`remove-add-beneficiary-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <p className="text-sm font-medium mb-3">Beneficiary {index + 1}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Full Name</Label>
                          <Input 
                            value={ben.full_name || ''} 
                            onChange={(e) => {
                              const updated = [...addMemberForm.beneficiaries];
                              updated[index] = {...updated[index], full_name: e.target.value};
                              setAddMemberForm({...addMemberForm, beneficiaries: updated});
                            }}
                            placeholder="Full name"
                            data-testid={`add-ben-${index}-name`}
                          />
                        </div>
                        <div>
                          <Label>Relationship</Label>
                          <Input 
                            value={ben.relationship || ''} 
                            onChange={(e) => {
                              const updated = [...addMemberForm.beneficiaries];
                              updated[index] = {...updated[index], relationship: e.target.value};
                              setAddMemberForm({...addMemberForm, beneficiaries: updated});
                            }}
                            placeholder="e.g., Child, Spouse"
                            data-testid={`add-ben-${index}-relationship`}
                          />
                        </div>
                        <div>
                          <Label>Date of Birth</Label>
                          <Input 
                            type="date"
                            value={ben.date_of_birth || ''} 
                            onChange={(e) => {
                              const updated = [...addMemberForm.beneficiaries];
                              const dob = e.target.value;
                              let age = 0;
                              if (dob) {
                                const birthDate = new Date(dob);
                                const today = new Date();
                                age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
                                if (age < 0) age = 0;
                              }
                              updated[index] = {...updated[index], date_of_birth: dob, age};
                              setAddMemberForm({...addMemberForm, beneficiaries: updated});
                            }}
                            data-testid={`add-ben-${index}-dob`}
                          />
                        </div>
                        <div>
                          <Label>Age</Label>
                          <Input 
                            type="number"
                            value={ben.age || 0} 
                            onChange={(e) => {
                              const updated = [...addMemberForm.beneficiaries];
                              updated[index] = {...updated[index], age: parseInt(e.target.value) || 0};
                              setAddMemberForm({...addMemberForm, beneficiaries: updated});
                            }}
                            placeholder="Auto-calculated"
                            data-testid={`add-ben-${index}-age`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex gap-2 pt-4 border-t">
            <Button 
              onClick={handleAddMember} 
              disabled={addingMember || !addMemberForm.first_name || !addMemberForm.last_name || !addMemberForm.email || !addMemberForm.contact_number}
              data-testid="confirm-add-member-btn"
            >
              {addingMember ? 'Adding...' : 'Add Member'}
            </Button>
            <Button variant="outline" onClick={() => setAddMemberOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={bulkImportOpen} onOpenChange={closeBulkImport}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Import Members
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <h4 className="font-semibold">Instructions:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Download the CSV template below</li>
                <li>Fill in member data following the template format</li>
                <li>Upload the completed CSV file</li>
                <li>Members will be created with default password: <code className="bg-background px-1 rounded">C4C + first 6 chars of NRC/Passport</code></li>
                <li>Members will be required to change password on first login</li>
              </ol>
            </div>

            {/* Download Template */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">CSV Template</p>
                <p className="text-sm text-muted-foreground">Download the template with all required columns</p>
              </div>
              <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>Upload CSV File</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  data-testid="csv-file-input"
                  className="flex-1"
                />
                {importFile && (
                  <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                    {importFile.name}
                  </span>
                )}
              </div>
            </div>

            {/* Import Result */}
            {importResult && (
              <div className="space-y-3">
                {/* Summary */}
                <div className={`p-4 rounded-lg ${importResult.imported_count > 0 && importResult.error_count === 0 ? 'bg-green-50 border border-green-200' : importResult.error_count > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {importResult.imported_count > 0 ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                    )}
                    <span className="font-semibold">Import Results</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total Rows:</span>
                      <p className="font-semibold">{importResult.total_rows || 0}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Imported:</span>
                      <p className="font-semibold text-green-600">{importResult.imported_count || 0}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Errors:</span>
                      <p className="font-semibold text-red-600">{importResult.error_count || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Imported Members */}
                {importResult.imported_members && importResult.imported_members.length > 0 && (
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold text-sm mb-2 text-green-600">Successfully Imported Members:</h4>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResult.imported_members.slice(0, 10).map((m, idx) => (
                        <div key={idx} className="text-xs flex justify-between bg-green-50 px-2 py-1 rounded">
                          <span>{m.name}</span>
                          <span className="text-muted-foreground">{m.email}</span>
                        </div>
                      ))}
                      {importResult.imported_members.length > 10 && (
                        <p className="text-xs text-muted-foreground">...and {importResult.imported_members.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Error List */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                    <h4 className="font-semibold text-sm mb-2 text-red-600">Errors ({importResult.errors.length}):</h4>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {importResult.errors.map((error, idx) => (
                        <p key={idx} className="text-xs text-red-700 bg-white px-2 py-1 rounded">{error}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeBulkImport}>
                {importResult ? 'Close' : 'Cancel'}
              </Button>
              {!importResult && (
                <Button 
                  onClick={handleBulkImport} 
                  disabled={importing || !importFile}
                  data-testid="start-import-btn"
                >
                  {importing ? 'Importing...' : 'Import Members'}
                </Button>
              )}
              {importResult && importResult.error_count > 0 && (
                <Button 
                  onClick={() => {
                    setImportFile(null);
                    setImportResult(null);
                  }}
                  variant="secondary"
                >
                  Try Again
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkInviteOpen} onOpenChange={setBulkInviteOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-invite-dialog">
          <DialogHeader>
            <DialogTitle>Create Missing Logins &amp; Invite</DialogTitle>
            <DialogDescription>
              Provision member logins and email each one a welcome invite with their temporary password.
            </DialogDescription>
          </DialogHeader>
          {!missingLogins ? (
            <p className="text-sm text-muted-foreground py-4">Checking member accounts…</p>
          ) : missingLogins.count === 0 ? (
            <p className="text-sm py-4" data-testid="bulk-invite-none">
              Every member already has a login account. Nothing to do.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm" data-testid="bulk-invite-count">
                <span className="font-semibold">{missingLogins.count}</span> member(s) have no login account.
                Each will get an account with a temporary password and a welcome email containing their
                login link.
              </p>
              <div className="max-h-52 overflow-y-auto border rounded divide-y">
                {missingLogins.members.map((m) => (
                  <div key={m.member_id} className="px-3 py-2 text-xs flex justify-between gap-2">
                    <span className="font-medium truncate">{m.name || m.member_id}</span>
                    <span className="text-muted-foreground truncate">{m.email || 'no email'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkInviteOpen(false)} data-testid="bulk-invite-cancel">
              Cancel
            </Button>
            {missingLogins?.count > 0 && (
              <Button onClick={runBulkInvite} disabled={bulkBusy} data-testid="bulk-invite-confirm">
                {bulkBusy ? 'Creating…' : `Create ${missingLogins.count} Login(s) & Send Invites`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminMembers;
